'use client';

import { useLocale, useTranslations } from "next-intl";
import { useActionState, useState } from "react";
import { registerAction } from "@/lib/auth/register";
import { redirect, Link } from "@/i18n/navigation";
import { registerData } from "@/types/api";

export default function RegisterForm() {
  const t = useTranslations("auth");
  const locale = useLocale();

  const [form, setForm] = useState<registerData>({
    license: "",
    password: "",
    email: "",
    firstname: "",
    lastname: "",
    birthdate: "",
    confirmPassword: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const [state, action, pending] = useActionState(registerAction, undefined);

  if (state?.success) redirect({ locale, href: "/dashboard" })

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center p-4 bg-primary">
      <form action={action} className="w-full max-w-lg animate-scale-in">
        {/* Brand header */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <svg
            className="w-10 h-10 text-accent-1"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
          </svg>
          <h1 className="text-2xl font-black tracking-tight">
            Tot<span className="text-accent-1">Scorer</span>
          </h1>
          <p className="text-sm text-gray-400">{t("register")}</p>
        </div>

        <div className="centered-form-container max-w-lg">
          {/* Name row — 2 columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-field">
              <label htmlFor="lastname" className="form-label">{t("lastname")}</label>
              <input
                type="text" id="lastname" name="lastname" className="forminput"
                autoComplete="family-name" onChange={handleChange} value={form.lastname} required
              />
            </div>
            <div className="form-field">
              <label htmlFor="firstname" className="form-label">{t("firstname")}</label>
              <input
                type="text" id="firstname" name="firstname" className="forminput"
                autoComplete="given-name" onChange={handleChange} value={form.firstname} required
              />
            </div>
          </div>

          {/* License */}
          <div className="form-field">
            <label htmlFor="license" className="form-label">{t("license")}</label>
            <input
              type="text" id="license" name="license" className="forminput"
              autoComplete="username" onChange={handleChange} value={form.license} required
            />
          </div>

          {/* Email */}
          <div className="form-field">
            <label htmlFor="email" className="form-label">{t("email")}</label>
            <input
              type="email" id="email" name="email" className="forminput"
              autoComplete="email" onChange={handleChange} value={form.email} required
            />
          </div>

          {/* Password row — 2 columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-field">
              <label htmlFor="password" className="form-label">{t("password")}</label>
              <input
                type="password" id="password" name="password" className="forminput"
                autoComplete="new-password" onChange={handleChange} value={form.password} required
              />
            </div>
            <div className="form-field">
              <label htmlFor="confirmPassword" className="form-label">{t("confirmPassword")}</label>
              <input
                type="password" id="confirmPassword" name="confirmPassword" className="forminput"
                autoComplete="new-password" onChange={handleChange} value={form.confirmPassword} required
              />
            </div>
          </div>

          {/* Birthdate */}
          <div className="form-field">
            <label htmlFor="birthdate" className="form-label">{t("birthdate")}</label>
            <input
              type="date" id="birthdate" name="birthdate" className="forminput"
              autoComplete="bday" onChange={handleChange} value={form.birthdate} required
            />
          </div>

          {/* Error */}
          {state && !state.success && state.error && (
            <p className="text-error text-sm font-medium">{state.error}</p>
          )}

          {/* Login link */}
          <p className="text-sm text-gray-400">
            {t("have_account")}&nbsp;
            <Link href="/login" className="link font-medium">{t("login")}</Link>
          </p>

          {/* Submit */}
          <button type="submit" className="submit-button" disabled={pending}>
            {pending ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                {t("wait")}
              </span>
            ) : t("register")}
          </button>
        </div>
      </form>
    </div>
  )
}
